import { ActionButton } from '@augmentt-dev/ui-components'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Alert } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import DetailRow from '../../../../../components/DetailRow'
import { getM365AccountStatusMessageId, getShownM365AccountStatus } from '../../../../../util/m365AccountStatus.util'
import { getLoginOptions, getUserLoginOptions } from '../../AddEditUserModal/utils'
import { Section } from '../../../../../components/ModalSidebar'
import PermissionDetails from './PermissionDetails/PermissionDetails'
import styles from './SystemUserTab.module.scss'

const getCompanyAccessValue = (user, collection) => {
  if (user.collectionId && collection) {
    return <FormattedMessage id="COMPANIES.NAME_COLLECTION" values={{ name: collection.owner?.name }} />
  }
  if (user.customersCount === user.totalCustomersCount) {
    return <FormattedMessage id="ADMINISTRATION.ALLCOMPANIES" />
  }
  if (!user.customersNames?.length) {
    return ''
  }
  return (
    <ul className={styles.companyList}>
      {user.customersNames.map((customerName) => (
        <li key={customerName}>{customerName}</li>
      ))}
    </ul>
  )
}

/**
 * System User tab of the user details panel: the Augmentt account settings Edit System User manages.
 */
function SystemUserTab({
  user,
  roleLabel = '',
  collection = null,
  permissionLevels,
  permissionEntities,
  canEdit = false,
  onEdit,
  isTechnicalPrimary = false,
  isTechnicalPrimaryDisabled = false,
  onToggleTechnicalPrimary = null,
}) {
  const intl = useIntl()
  const isOwner = user.chargebee_id != null
  // Users are linked by matching emails, ignoring case. Their Augmentt sign-in email isn't changed to follow.
  const hasEmailMismatch = !!user.m365Email && user.m365Email.toLowerCase() !== user.email?.toLowerCase()
  const accountStatusMessageId = getM365AccountStatusMessageId(
    getShownM365AccountStatus(user.m365Properties?.accountStatus, isTechnicalPrimary),
  )

  // Technical Primary is a role the person holds alongside their user type, so it reads as part of it.
  const roleValue = useMemo(
    () =>
      isTechnicalPrimary
        ? intl.formatList([roleLabel, intl.formatMessage({ id: 'TECHNICAL_PRIMARY.TITLE' })], { type: 'unit' })
        : roleLabel,
    [intl, isTechnicalPrimary, roleLabel],
  )

  const loginOptionsValue = useMemo(() => {
    const labels = Object.fromEntries(getLoginOptions(intl).map((option) => [option.value, option.label]))
    return getUserLoginOptions(user)
      .filter(Boolean)
      .map((loginOption) => labels[loginOption] ?? loginOption)
      .join(', ')
  }, [intl, user])

  return (
    <>
      <div className={styles.toolbar}>
        <ActionButton
          id="system-user-edit-btn"
          icon="fas fa-pen-to-square"
          label={<FormattedMessage id="GENERAL.EDIT" />}
          variant="primary"
          disabled={!canEdit}
          onClick={onEdit}
        />
        {onToggleTechnicalPrimary && (
          <ActionButton
            icon={isTechnicalPrimary ? 'fas fa-user-minus' : 'fas fa-user-check'}
            label={
              <FormattedMessage id={isTechnicalPrimary ? 'TECHNICAL_PRIMARY.REMOVE' : 'TECHNICAL_PRIMARY.ASSIGN'} />
            }
            disabled={isTechnicalPrimaryDisabled}
            onClick={onToggleTechnicalPrimary}
          />
        )}
      </div>

      <div className="px-4 py-4">
        <Section title="SYSTEM_USER_PANEL.ACCOUNT">
          <DetailRow
            size="sm"
            label={<FormattedMessage id="SYSTEM_USER_PANEL.FIRST_NAME" />}
            value={user.first_name ?? ''}
          />
          <DetailRow
            size="sm"
            label={<FormattedMessage id="SYSTEM_USER_PANEL.LAST_NAME" />}
            value={user.last_name ?? ''}
          />
          <DetailRow size="sm" label={<FormattedMessage id="SYSTEM_USER_PANEL.EMAIL" />} value={user.email ?? ''} />
          {!user.microsoft365Linked && (
            <>
              <DetailRow size="sm" label={<FormattedMessage id="FORM.JOB_TITLE" />} value={user.job_title ?? ''} />
              <DetailRow size="sm" label={<FormattedMessage id="FORM.DEPARTMENT" />} value={user.department ?? ''} />
              <DetailRow size="sm" label={<FormattedMessage id="FORM.PHONE" />} value={user.phone ?? ''} />
              <DetailRow
                size="sm"
                label={<FormattedMessage id="FORM.OFFICE_LOCATION" />}
                value={user.office_location ?? ''}
              />
            </>
          )}
          <DetailRow size="sm" label={<FormattedMessage id="SYSTEM_USER_PANEL.ROLE" />} value={roleValue} />
          <DetailRow
            size="sm"
            label={<FormattedMessage id="SYSTEM_USER_PANEL.MICROSOFT_365" />}
            value={
              <FormattedMessage
                id={user.microsoft365Linked ? 'SYSTEM_USER_PANEL.LINKED' : 'SYSTEM_USER_PANEL.NOT_LINKED'}
              />
            }
          />
          {accountStatusMessageId && (
            <DetailRow
              size="sm"
              label={<FormattedMessage id="SYSTEM_USER_PANEL.M365_ACCOUNT_STATUS" />}
              value={<FormattedMessage id={accountStatusMessageId} />}
            />
          )}
          {hasEmailMismatch && (
            <Alert variant="warning" className="mb-0 mt-2">
              {/* Alerts are flex containers, so one span keeps the text and email flowing as one line. */}
              <span>
                <FormattedMessage
                  id="SYSTEM_USER_PANEL.M365_EMAIL_MISMATCH"
                  values={{ m365Email: <strong>{user.m365Email}</strong> }}
                />
              </span>
            </Alert>
          )}
        </Section>

        <Section title="SYSTEM_USER_PANEL.SIGN_IN">
          <DetailRow
            size="sm"
            label={<FormattedMessage id="SYSTEM_USER_PANEL.LOGIN_OPTIONS" />}
            value={loginOptionsValue}
          />
        </Section>

        <Section title="SYSTEM_USER_PANEL.COMPANY_ACCESS">
          <DetailRow
            size="sm"
            label={<FormattedMessage id="SYSTEM_USER_PANEL.COMPANIES" />}
            value={getCompanyAccessValue(user, collection)}
          />
        </Section>

        <Section title="SYSTEM_USER_PANEL.PERMISSIONS" showSeparatorLine={false}>
          {isOwner ? (
            <p className="text-color-secondary mb-0">
              <FormattedMessage id="SYSTEM_USER_PANEL.OWNER_PERMISSIONS" />
            </p>
          ) : (
            <PermissionDetails
              permissions={user.permissions}
              permissionLevels={permissionLevels}
              permissionEntities={permissionEntities}
            />
          )}
        </Section>
      </div>
    </>
  )
}

SystemUserTab.propTypes = {
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    chargebee_id: PropTypes.string,
    login_options: PropTypes.string,
    collectionId: PropTypes.number,
    customersCount: PropTypes.number,
    totalCustomersCount: PropTypes.number,
    customersNames: PropTypes.arrayOf(PropTypes.string),
    permissions: PropTypes.objectOf(PropTypes.number),
    /** Whether the user is linked to the Microsoft 365 user with the same email in the MSP's own tenant */
    microsoft365Linked: PropTypes.bool,
    /** Entered by hand for a user who isn't linked */
    job_title: PropTypes.string,
    department: PropTypes.string,
    phone: PropTypes.string,
    office_location: PropTypes.string,
    /** The linked Microsoft 365 user's email (sign-in name); null when not linked or deleted from the tenant */
    m365Email: PropTypes.string,
    /** The linked Microsoft 365 user's properties from the latest snapshot; the status is unknown without one */
    m365Properties: PropTypes.shape({ accountStatus: PropTypes.string }),
  }).isRequired,
  roleLabel: PropTypes.string,
  collection: PropTypes.shape({ owner: PropTypes.shape({ name: PropTypes.string }) }),
  permissionLevels: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  permissionEntities: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, module_name: PropTypes.string }))
    .isRequired,
  canEdit: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  isTechnicalPrimary: PropTypes.bool,
  isTechnicalPrimaryDisabled: PropTypes.bool,
  /** Assigns or removes the Technical Primary role; omitted when the viewer cannot change it */
  onToggleTechnicalPrimary: PropTypes.func,
}

export default React.memo(SystemUserTab)
